import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useWebSocket } from "@/lib/ws";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * Global notification listener: subscribes to WS new_message events
 * and surfaces toasts according to the spec rules:
 *   - DMs: always notify both parties
 *   - @mentions: always notify mentioned person
 *   - #managers messages: notify all managers
 *   - #sales-floor general: NO notification (ambient)
 */
export default function ChatNotifier() {
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const isManager = ["floor_manager", "sales_manager", "gsm"].includes(user?.role);

  useWebSocket((msg) => {
    if (!user) return;
    if (msg.event !== "new_message") return;
    const { channel_id, message, notify_type, recipients, sender_id } = msg.payload || {};
    if (!message) return;
    if (sender_id === user.user_id) return; // never toast own messages
    if (loc.pathname === "/chat" && new URLSearchParams(loc.search).get("c") === channel_id) return;

    const mentioned = (message.mentions || []).includes(user.user_id);
    let shouldToast = false;
    let label = "";

    if (notify_type === "dm" && (recipients || []).includes(user.user_id)) {
      shouldToast = true; label = `DM from ${message.sender_name}`;
    } else if (mentioned) {
      shouldToast = true; label = `${message.sender_name} mentioned you`;
    } else if (notify_type === "managers" && isManager) {
      shouldToast = true; label = `#managers · ${message.sender_name}`;
    }

    if (!shouldToast) return;
    toast(label, {
      description: message.text.length > 80 ? message.text.slice(0, 80) + "…" : message.text,
      action: {
        label: "Open",
        onClick: () => nav(`/chat?c=${encodeURIComponent(channel_id)}`),
      },
      duration: 6000,
    });
  });
  return null;
}
